import { TreeDataItem, TreeView } from "@/components/tree-view";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityCard, EntityCardSkeleton } from "@cohesive/ui-components";
import { useDebounce } from "@uidotdev/usehooks";
import { AtSign, Building, CheckSquare, Folder, Users } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorEmpty,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";
import { useAsanaMCPProjects } from "./useAsanaMCPProjects";
import { useAsanaMCPTasksSearch } from "./useAsanaMCPTasksSearch";
import { useAsanaMCPTeams } from "./useAsanaMCPTeams";
import { useAsanaMCPWorkspaces } from "./useAsanaMCPWorkspaces";

interface AsanaTreeDataItem extends TreeDataItem {
  workspaceGid?: string;
  teamGid?: string;
  projectGid?: string;
  isMyTasks?: boolean; // Special flag for "My Tasks" node
}

export const AsanaMCPSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [selectedTreeItem, setSelectedTreeItem] =
    useState<AsanaTreeDataItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 500);

  // Load workspaces
  const { data: workspacesData, isLoading: isWorkspacesLoading } =
    useAsanaMCPWorkspaces(connection);

  // Parse workspaces from entities
  const workspaces = useMemo(() => {
    if (!workspacesData) return [];
    return workspacesData.filter(
      (entity: any) => entity.base === "group" && entity.data,
    );
  }, [workspacesData]);

  // Load teams for selected workspace
  const selectedWorkspaceGid = useMemo(() => {
    // If a project or team is selected, find the workspace from the tree structure
    if (selectedTreeItem?.workspaceGid) {
      return selectedTreeItem.workspaceGid;
    }
    return undefined;
  }, [selectedTreeItem]);

  const { data: teamsData } = useAsanaMCPTeams({
    connection,
    workspaceGid: selectedWorkspaceGid,
  });

  const teams = useMemo(() => {
    if (!teamsData) return [];
    return teamsData.filter(
      (entity: any) => entity.base === "group" && entity.data,
    );
  }, [teamsData]);

  // Load projects for all teams that have been expanded
  // For simplicity, we'll load all teams' projects once workspace is selected
  const teamGidsForProjects = useMemo(() => {
    if (!selectedWorkspaceGid || !teams.length) return [];
    // Load all team projects eagerly when workspace is selected
    return teams.map((team: any) => team.data.id);
  }, [selectedWorkspaceGid, teams]);

  // Load projects for all teams (we'll make multiple requests)
  // This is a workaround since we can't use multiple useQueries easily
  // In production, you might want to batch this or use a different approach
  const { data: projectsDataTeam1 } = useAsanaMCPProjects({
    connection,
    teamGid: teamGidsForProjects[0],
  });
  const { data: projectsDataTeam2 } = useAsanaMCPProjects({
    connection,
    teamGid: teamGidsForProjects[1],
  });
  const { data: projectsDataTeam3 } = useAsanaMCPProjects({
    connection,
    teamGid: teamGidsForProjects[2],
  });

  // Create a map of team -> projects
  const projectsByTeam = useMemo(() => {
    const map: Record<string, any[]> = {};

    if (projectsDataTeam1 && teamGidsForProjects[0]) {
      map[teamGidsForProjects[0]] = projectsDataTeam1.filter(
        (entity: any) => entity.base === "project" && entity.data,
      );
    }
    if (projectsDataTeam2 && teamGidsForProjects[1]) {
      map[teamGidsForProjects[1]] = projectsDataTeam2.filter(
        (entity: any) => entity.base === "project" && entity.data,
      );
    }
    if (projectsDataTeam3 && teamGidsForProjects[2]) {
      map[teamGidsForProjects[2]] = projectsDataTeam3.filter(
        (entity: any) => entity.base === "project" && entity.data,
      );
    }

    return map;
  }, [
    projectsDataTeam1,
    projectsDataTeam2,
    projectsDataTeam3,
    teamGidsForProjects,
  ]);

  // Build tree data for sidebar with lazy loading
  const sidebarTreeData: AsanaTreeDataItem[] = useMemo(() => {
    return workspaces.map((workspace: any) => {
      const workspaceGid = workspace.data.id;
      const workspaceItem: AsanaTreeDataItem = {
        id: `workspace-${workspaceGid}`,
        name: workspace.data.name || "Unnamed Workspace",
        icon: Building,
        workspaceGid,
        onClick: () =>
          setSelectedTreeItem({
            id: `workspace-${workspaceGid}`,
            name: workspace.data.name || "Unnamed Workspace",
            workspaceGid,
          }),
      };

      // Add "My Tasks" special node
      const myTasksNode: AsanaTreeDataItem = {
        id: `workspace-${workspaceGid}-my-tasks`,
        name: "My Tasks",
        icon: CheckSquare,
        workspaceGid,
        isMyTasks: true,
        onClick: () =>
          setSelectedTreeItem({
            id: `workspace-${workspaceGid}-my-tasks`,
            name: "My Tasks",
            workspaceGid,
            isMyTasks: true,
          }),
      };

      // Add teams if this workspace is selected
      const teamNodes: AsanaTreeDataItem[] = [];
      if (selectedWorkspaceGid === workspaceGid && teams.length > 0) {
        teams.forEach((team: any) => {
          const teamGid = team.data.id;
          const teamNode: AsanaTreeDataItem = {
            id: `team-${teamGid}`,
            name: team.data.name || "Unnamed Team",
            icon: Users,
            workspaceGid,
            teamGid,
            onClick: () =>
              setSelectedTreeItem({
                id: `team-${teamGid}`,
                name: team.data.name || "Unnamed Team",
                workspaceGid,
                teamGid,
              }),
          };

          // Add projects if we have them loaded for this team
          const teamProjects = projectsByTeam[teamGid] || [];
          if (teamProjects.length > 0) {
            teamNode.children = teamProjects.map((project: any) => {
              const projectGid = project.data.id;
              return {
                id: `project-${projectGid}`,
                name: project.data.name || "Unnamed Project",
                icon: Folder,
                workspaceGid,
                teamGid,
                projectGid,
                onClick: () =>
                  setSelectedTreeItem({
                    id: `project-${projectGid}`,
                    name: project.data.name || "Unnamed Project",
                    workspaceGid,
                    teamGid,
                    projectGid,
                  }),
                actions: [
                  <Button
                    aria-label="Reference in chat"
                    variant="ghost"
                    size="icon"
                    className="!p-2 rounded-full w-5 h-5"
                    onClick={(e) => {
                      e.stopPropagation();
                      return onSelect(project);
                    }}
                  >
                    <AtSign className="!size-3.5" />
                  </Button>,
                ],
              };
            });
          }

          teamNodes.push(teamNode);
        });
      }

      workspaceItem.children = [myTasksNode, ...teamNodes];
      return workspaceItem;
    });
  }, [
    workspaces,
    teams,
    projectsByTeam,
    selectedWorkspaceGid,
    setSelectedTreeItem,
  ]);

  // Set initial selected item to first workspace
  React.useEffect(() => {
    if (!selectedTreeItem && sidebarTreeData.length > 0) {
      const firstWorkspace = sidebarTreeData[0];
      setSelectedTreeItem(firstWorkspace);
    }
  }, [selectedTreeItem, sidebarTreeData]);

  // Search tasks with current context
  // Only load tasks when:
  // 1. A project is selected (projectGid)
  // 2. "My Tasks" is selected (isMyTasks)
  const shouldLoadTasks =
    !!selectedTreeItem?.projectGid || !!selectedTreeItem?.isMyTasks;

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAsanaMCPTasksSearch({
    connection,
    workspaceGid: selectedTreeItem?.workspaceGid,
    projectGid: selectedTreeItem?.projectGid,
    isMyTasks: selectedTreeItem?.isMyTasks,
    query: debouncedQuery,
    enabled: shouldLoadTasks,
  });

  // Flatten all results from all pages
  const results = data?.pages?.flatMap((page) => page.data) ?? [];

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed task.
  const genericById = useMemo(
    () =>
      buildGenericById(
        data?.pages?.flatMap((page) => toGenericArray(page.data_generic)) ?? [],
      ),
    [data],
  );

  // Handle scroll to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const handleTaskSelect = (task: any) => {
    onSelect(task);
  };

  const resultIds = useMemo(
    () => results.map((t: any) => t.data?.id ?? String(t)),
    [results],
  );

  return (
    <Selector>
      <SelectorSidebar>
        {isWorkspacesLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView
            data={sidebarTreeData}
            onSelectChange={(item) =>
              setSelectedTreeItem((item as AsanaTreeDataItem) || null)
            }
            expandAll={false}
          />
        )}
      </SelectorSidebar>
      <SelectorSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={`Search ${selectedTreeItem?.name || "Asana"}...`}
        autoFocus
        disabled={!selectedTreeItem}
      />
      <SelectorResults
        onScroll={handleScroll}
        style={{ paddingBottom: "200px" }}
      >
        {!selectedTreeItem ? (
          <SelectorEmpty message="Select a workspace, project, or My Tasks to view tasks" />
        ) : selectedTreeItem.teamGid && !selectedTreeItem.projectGid ? (
          <SelectorEmpty message="Expand this team and select a project to view tasks" />
        ) : !shouldLoadTasks ? (
          <SelectorEmpty message="Select a project or My Tasks to view tasks" />
        ) : isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <EntityCardSkeleton
                key={i}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            ))}
          </div>
        ) : isError ? (
          <SelectorEmpty message="Error loading tasks" />
        ) : results.length === 0 ? (
          <SelectorEmpty
            message={
              debouncedQuery
                ? "No tasks found matching your search"
                : selectedTreeItem.isMyTasks
                  ? "You have no tasks assigned in this workspace"
                  : "No tasks found in this project"
            }
          />
        ) : (
          <div className="divide-y">
            {results.map((task: any) => (
              <EntityDragRow
                key={task.data?.id ?? String(task)}
                entity={task}
                generic={genericById.get(task.data?.id ?? String(task))}
                entityId={task.data?.id ?? String(task)}
                orderedIds={resultIds}
                onSelect={handleTaskSelect}
                dragScope="asana-mcp"
              >
                <EntityCard
                  entity={task}
                  className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                />
              </EntityDragRow>
            ))}
            {/* Loading indicator for next page */}
            {isFetchingNextPage && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <EntityCardSkeleton
                    key={`loading-${i}`}
                    className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </SelectorResults>
    </Selector>
  );
};

export default AsanaMCPSelector;
